import { Controller } from "stimulus";
import SupercastUploadManager, { isBackgroundUploadEnabled, logUploadState as postLogUploadState } from "packs/episode_upload_manager";
import { loadUppy } from "utils/loadUppy";

const ALLOWED_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
    "video/mp4",
    "video/quicktime"
];

const AUDIO_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
];

const VIDEO_TYPES = [
    "video/mp4",
    "video/quicktime",
];

function extractDuration(file, onResolved) {
    const objectURL = URL.createObjectURL(file);
    const mediaEl = document.createElement("audio");
    mediaEl.src = objectURL;
    mediaEl.onloadedmetadata = () => {
        onResolved(mediaEl.duration);
        URL.revokeObjectURL(objectURL);
    };
}

export default class UploadController extends Controller {
    // Per-media-type upload state, and the only thing the header pill and the progress rows read.
    // Set here rather than in connect() because mediaStatusTargetConnected seeds it from the
    // server render, and target callbacks can land before connect() has run.
    // A kind is null while JS has no opinion on it (leave whatever the server rendered alone),
    // then {state: "uploading"|"uploaded"|"error"|"absent"} once it does.
    initialize() {
        this._mediaStatus = { video: null, audio: null };
    }

    connect() {
        this.uploading = [];
        this._uploadStats = {};
        // Tracks the active localId per media type so pause/resume/retry actions can target the
        // right upload without passing localId through the DOM.
        this._managedLocalIds = { video: null, audio: null };

        // Behind the `background_episode_upload` flag (exposed via the persistent
        // `data-background-episode-upload-enabled` attribute in creator_dashboard.html.erb),
        // uploads are driven by the SupercastUploadManager singleton + a dedicated Worker instead
        // of Uppy, so the transfer survives this controller disconnecting (modal close / Turbo
        // navigation). `this.files` mirrors the small subset of Uppy's per-file shape
        // ({id, name, type, data, meta}) that the shared preview/progress helpers below rely on -
        // it's only populated in this code path.
        this.files = [];
        this.useManager = isBackgroundUploadEnabled();

        if (this.useManager) {
            this._unsubscribeManager = SupercastUploadManager.subscribe(this.handleManagerEvent.bind(this));
            this.reattachInFlightUploads();
        } else {
            // Kick off the load as soon as this controller mounts, so window.Uppy is likely
            // already resolved by the time the creator drops/selects a file. Failures are
            // surfaced (and retried) at the individual call sites below via ensureUppy(), not
            // here - this .catch() only exists to avoid an unhandled promise rejection.
            this.ensureUppy().catch(() => {});
        }

        this.extracting = false;
        if (this.hasEpisodeIdValue && this.episodeIdValue) {
            this.checkExtractionStatusAndMaybePoll();
        }
    }

    disconnect() {
        if (this._unsubscribeManager) {
            this._unsubscribeManager();
            this._unsubscribeManager = null;
        }
    }

    // Lazily loads window.Uppy (a 520K vendored script most pages don't declare a <script> tag
    // for - see utils/loadUppy.js) and constructs this.uppy on top of it. Memoized on the
    // instance so repeated calls (connect(), then a later addOrReplaceFile()/removeMedia()) share
    // the same in-flight/settled promise, but cleared on failure so a later call retries instead
    // of awaiting a permanently-rejected promise.
    ensureUppy() {
        if (!this.uppyReady) {
            this.uppyReady = loadUppy()
                .then(() => {
                    this.uppy = new window.Uppy.Uppy(this.uppyConfig());
                    this.attachTus(this.uppy);
                })
                .catch((error) => {
                    this.uppyReady = null;
                    throw error;
                });
        }
        return this.uppyReady;
    }

    dropZoneTargetConnected(el) {
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        let icon = document.getElementById("cloud");

        el.addEventListener("dragover", prevent);
        el.addEventListener("dragenter", (e) => {
            prevent(e);
            icon.classList.add("text-blue-700");
            icon.classList.remove("text-gray-500");
        });
        el.addEventListener("dragleave", (e) => {
            prevent(e);
            icon.classList.add("text-gray-500");
            icon.classList.remove("text-blue-700");
        });

        el.addEventListener("drop", (e) => {
            e.preventDefault();

            const file = e.dataTransfer.files[0];
            this.addOrReplaceFile(file);
        });
    }

    eventTargetConnected(el) {
        el.addEventListener("upload-started", (e) => {
            const { episodeId } = e.detail;
            if (episodeId === null) return;

            window.Turbo.visit(this._detailPath(episodeId), { frame: "step" });
        });
    }

    previewVideoTargetConnected(el) {
        if (this.hasVideoImageInputTarget) {
            this.videoImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.videoImageInputTarget.files && this.videoImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        el.poster = fr.result;
                        el.load(); // Reset the video so the new thumbnail displays
                    }
                    fr.readAsDataURL(this.videoImageInputTarget.files[0]);
                }
            });
        }

        const video = this.getFileByType(VIDEO_TYPES);
        if (!video || !this.hasPreviewVideoTarget) return;

        this.updateVideoPreview(video);
    }

    previewAudioTargetConnected(el) {
        if (this.hasAudioImageInputTarget) {
            this.audioImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.audioImageInputTarget.files && this.audioImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        document.getElementById("preview-image").src = fr.result;
                    }
                    fr.readAsDataURL(this.audioImageInputTarget.files[0]);
                }
            });
        }

        const audio = this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]);
        if (!audio || !this.hasPreviewAudioTarget) return;

        this.updateAudioPreview(audio);
    }

    extractionNoticeTargetConnected(el) {
        this.toggleExtractionNotice();
    }

    // The pills re-render with each wizard step, so they arrive carrying the server's view of the
    // episode. Two things follow: what the server already has is the starting state for a kind JS
    // hasn't touched (without it, removing the video from a video+audio episode has no way to know
    // the audio is still there), and anything JS does know outranks it - stepping forward
    // mid-upload must not reset the pill to "Uploaded".
    mediaStatusTargetConnected(el) {
        for (const kind of ["video", "audio"]) {
            if (this._mediaStatus[kind]) continue;
            if (el.dataset[kind] === "true") this._mediaStatus[kind] = { state: "uploaded" };
        }
        this._renderMediaStatus();
    }

    videoOnlyTargetConnected(el) {
        const video = this.getFileByType(VIDEO_TYPES);
        if (!video) return;

        el.classList.remove("hidden");
    }

    audioOnlyTargetConnected(el) {
        const audio = this.getFileByType(AUDIO_TYPES);
        if (!audio) return;

        el.classList.remove("hidden");
    }

    toggleExtractionNotice() {
        if (this.hasExtractionNoticeTarget) {
            if (this.extracting) {
                this.extractionNoticeTarget.classList.remove('hidden');
                this.previewAudioTarget.parentElement.classList.add('hidden');
            } else {
                this.extractionNoticeTarget.classList.add('hidden');
            }
        }
    }

    updateAudioPreview(audio) {
        if (this.hasPreviewAudioTarget) {
            const audioEl = this.previewAudioTarget.querySelector("audio");
            audioEl.src = URL.createObjectURL(audio.data);
            this.previewAudioTarget.parentElement.classList.remove("hidden");
            this.addAudioTargets.forEach(t => t.classList.add("hidden"));
        }
    }

    updateVideoPreview(video) {
        if (this.hasPreviewVideoTarget) {
            const current = this.previewVideoTarget.querySelector("source");
            const source = document.createElement("source");
            source.src = URL.createObjectURL(video.data);
            source.type = video.type;
            if (current) {
                this.previewVideoTarget.removeChild(current);
            }
            this.previewVideoTarget.appendChild(source);
            this.previewVideoTarget.load(); // Force a reload after changing
            this.previewVideoTarget.parentElement.classList.remove("hidden");
            this.addVideoTargets.forEach(t => t.classList.add("hidden"));
        }
    }

    getFileByType(fileTypes) {
        // this.uppy may not exist yet - Uppy now loads lazily (see ensureUppy()) and a handful of
        // targetConnected callbacks call this synchronously, before that load can resolve. No
        // uppy means no file could have been added yet, so treat it the same as "no files".
        const files = this.useManager ? this.files : (this.uppy ? this.uppy.getFiles() : []);
        for (const file of files) {
            if (fileTypes.includes(file.type)) return file;
        }
        return null;
    }

    // Thin wrapper so call sites below don't need to know this.episodeIdValue - the actual
    // POST /log_upload logic is shared with SupercastUploadManager's own upload-state logging.
    logUploadState(upload_log_id, state, error_message = null) {
        postLogUploadState(this.episodeIdValue, upload_log_id, state, error_message);
    }

    uppyConfig() {
        return {
            autoProceed: true,
            onBeforeFileAdded: this.onBeforeFileAdded.bind(this),
        };
    }

    addFile(e) {
        const file = e.target.files[0];
        this.addOrReplaceFile(file);
    }

    // Shared entry point for both the drag/drop zone and the file-input `addFile` action -
    // branches into the legacy Uppy flow or the manager-driven flow depending on the flag.
    async addOrReplaceFile(file) {
        if (this.useManager) {
            this.addFileManaged(file);
            return;
        }

        try {
            await this.ensureUppy();
        } catch (error) {
            if (this.hasErrorTarget) {
                this.errorTarget.innerText = "Couldn't load the uploader. Please refresh and try again.";
            }
            return;
        }

        let replace;
        if (VIDEO_TYPES.includes(file.type)) {
            replace = this.getFileByType(VIDEO_TYPES);
        } else {
            replace = this.getFileByType(AUDIO_TYPES);
        }
        if (replace) {
            this.uppy.removeFile(replace.id); // aborts the in-flight tus transfer for the displaced file, if any
            this.purgeReplacedMedia(replace);
        }
        this.uppy.addFile(file);
    }

    exit(e) {
        // The legacy Uppy path uploads on the main thread for as long as this controller/modal is
        // mounted, so closing mid-upload really does risk losing it - keep warning there. With
        // the manager-driven flow the transfer survives the modal closing entirely (that's the
        // whole point of this change), so there's nothing to warn about.
        if (!this.useManager && this.uploading.length > 0) {
            if (!window.confirm("Exiting now will cancel in progress uploads.\nAre you sure you want to close?")) {
                e.stopImmediatePropagation();
                return;
            }
        }

        window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}`);
    }

    async removeAudio(e) {
        await this.removeMedia({
            kind: "audio",
            file: this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]),
            endpoint: "remove_audio",
            previewTarget: this.previewAudioTarget,
            onlyTargets: this.audioOnlyTargets,
            addTargets: this.addAudioTargets,
        });
    }

    async removeVideo(e) {
        await this.removeMedia({
            kind: "video",
            file: this.getFileByType(VIDEO_TYPES),
            endpoint: "remove_video",
            previewTarget: this.previewVideoTarget,
            onlyTargets: this.videoOnlyTargets,
            addTargets: this.addVideoTargets,
        });
    }

    // Shared by removeAudio/removeVideo - both drop the local file reference (cancelling any
    // in-flight upload for it), then tell the server to clear the committed media once nothing
    // else is still uploading. Differ only in which media type/endpoint/targets they touch.
    async removeMedia({ kind, file, endpoint, previewTarget, onlyTargets, addTargets }) {
        let uploadLogId, episodeId;

        if (file) {
            ({ uploadLogId, episodeId } = this.uploadLogInfoFor(file));

            if (this.useManager) {
                if (file.meta.localId) SupercastUploadManager.cancel(file.meta.localId);
                this.files.splice(this.files.indexOf(file), 1);
                this.uploading = this.uploading.filter((id) => id !== file.meta.localId);
            } else {
                await this.ensureUppy();
                this.uppy.removeFile(file.id);
                this.uploading = this.uploading.filter((id) => id !== uploadLogId);
            }
        }

        if (this.uploading.length == 0) {
            await this.purgeMedia(endpoint, { uploadLogId, episodeId });
        }

        previewTarget.parentElement.classList.add("hidden");
        onlyTargets.forEach(t => t.classList.add("hidden"));
        // The add control is hidden as soon as a preview goes up, so removal is the only thing
        // that can put it back - without this there'd be no way to add media again short of a reload.
        addTargets.forEach(t => t.classList.remove("hidden"));
        this._settleMedia(kind, "absent");
    }

    // Read-only lookup of the upload_log_id (and, under the manager path, the resolved episodeId)
    // tracked for a locally-known file - so callers can tell the server which UploadLog to cancel
    // without duplicating the manager-vs-legacy branching everywhere. Doesn't touch any local
    // tracking state itself.
    uploadLogInfoFor(file) {
        if (!file) return {};

        if (this.useManager && file.meta.localId) {
            const tracked = SupercastUploadManager.getUpload(file.meta.localId);
            return {
                uploadLogId: file.meta.upload_log_id || (tracked && tracked.uploadLogId),
                episodeId: tracked && tracked.episodeId,
            };
        }

        return { uploadLogId: file.meta && file.meta.upload_log_id };
    }

    // The actual DELETE request - the same request path used by the explicit remove button
    // (removeMedia) and the replace flow (purgeReplacedMedia), so both hit the exact same
    // controller action/behavior (Dashboard::Channels::EpisodesController#remove_audio/
    // #remove_video), which also cancels the passed-in upload_log_id server-side if it's still open.
    async purgeMedia(endpoint, { uploadLogId, episodeId } = {}) {
        const targetEpisodeId = episodeId || (this.hasEpisodeIdValue ? this.episodeIdValue : null);
        if (!targetEpisodeId) return;

        const csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);
        if (uploadLogId) formData.append("upload_log_id", uploadLogId);

        const payload = {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
            },
            body: formData
        };

        await fetch(`/dashboard/channels/episodes/${targetEpisodeId}/${endpoint}`, payload);
    }

    // Cancels the upload a new file selection is about to displace - aborts any in-flight
    // transfer (worker postMessage under the manager path; the tus request is already aborted by
    // uppy.removeFile under the legacy path, see addOrReplaceFile) and tells the server to purge
    // the attachment + cancel its UploadLog (if still open), so a reupload can never leave an
    // orphaned upload_log/attachment behind. Shared by addOrReplaceFile (legacy path) and
    // addFileManaged (manager path) - covers both replacing a file mid-upload and replacing an
    // already-fully-attached one (in the latter case there's no open upload_log to cancel, so this
    // degrades to the same attachment purge remove_audio/remove_video already do).
    purgeReplacedMedia(replaced) {
        const { uploadLogId, episodeId } = this.uploadLogInfoFor(replaced);

        if (this.useManager && replaced.meta.localId) {
            SupercastUploadManager.cancel(replaced.meta.localId);
        }
        this.uploading = this.uploading.filter((id) => id !== (replaced.meta.localId || uploadLogId));

        const endpoint = VIDEO_TYPES.includes(replaced.type) ? "remove_video" : "remove_audio";
        this.purgeMedia(endpoint, { uploadLogId, episodeId });
    }

    // Carries release_plan forward so the card pre-selection survives the upload -> detail hop.
    // Prefers the controller root over window.location: modal-component injects the wizard by fetch
    // and never changes the browser URL, so window.location.search here is the host page's. The
    // fallback covers ama_episodes/selection.html.erb, the third root instantiating this controller,
    // which renders no release-plan value at all - hasReleasePlanValue is false there whether the
    // param was present or absent, so it can't be used to distinguish "no plan" from "not rendered";
    // an empty releasePlanValue simply falls through to the URL fallback like the missing case does.
    _detailPath(episodeId) {
        const plan = this.releasePlanValue ||
            new URLSearchParams(window.location.search).get("release_plan");
        const query = plan ? `?release_plan=${encodeURIComponent(plan)}` : "";
        return `/dashboard/channels/episodes/${episodeId}/detail${query}`;
    }

    // Premieres need a video, so the premiere entry point narrows step 1. Gated on the step-1 drop
    // zone still being in the DOM: this controller's element sits outside <turbo-frame id="step">, so
    // it survives the hop to step 2, where _media.html.erb legitimately offers "Add audio".
    get videoOnlyStep() {
        return this.videoRequiredValue && this.hasDropZoneTarget;
    }

    get allowedFileTypes() {
        return this.videoOnlyStep ? VIDEO_TYPES : ALLOWED_TYPES;
    }

    fileTypeError(file) {
        return this.videoOnlyStep
            ? "Premieres need a video. Choose an MP4 or MOV file."
            : "Unsupported file type provided: " + file.type;
    }

    onBeforeFileAdded(file, files) {
        if (!this.allowedFileTypes.includes(file.type)) {
            if (this.hasErrorTarget) {
                this.errorTarget.innerText = this.fileTypeError(file);
            }
            return false
        }

        // Pull media duration for metadata.
        extractDuration(file.data, (duration) => { file.meta.duration = duration; });

        return true;
    }

    async fileAdded(file) {
        if (this.hasWorkingTarget) {
            this.workingTarget.classList.remove("hidden");
        }

        const mediaType = file.type.split("/")[0];

        const url = new URL(window.location.origin + "/dashboard/channels/episodes/presigned_upload");

        url.searchParams.append("type", mediaType);
        url.searchParams.append("title", file.name);
        url.searchParams.append("episode", this.episodeIdValue);
        if (this.hasDynamicValue && this.hasTriggerEventValue) {
            url.searchParams.append("dynamic", this.dynamicValue);
            url.searchParams.append("trigger_event", this.triggerEventValue);
        }

        await fetch(url.toString())
            .then(response => response.json())
            .then(data => {
                const { episode_id, upload_log_id, ...bunny } = data;
                this.episodeIdValue = episode_id;
                file.meta.bunny = bunny;
                file.meta.upload_log_id = upload_log_id;

                this.uploading.push(upload_log_id);

                if (this.hasEventTarget) {
                    const event = new CustomEvent("upload-started", { detail: { episodeId: this.episodeIdValue } });
                    this.eventTarget.dispatchEvent(event);
                }

                if (VIDEO_TYPES.includes(file.type)) {
                    this.updateVideoPreview(file)
                } else {
                    this.updateAudioPreview(file);
                }

                this.logUploadState(file.meta.upload_log_id, "upload_started");
            })
            .finally(() => {
                if (this.hasWorkingTarget) {
                    this.workingTarget.classList.remove("hidden");
                }
            });
    }

    // ---------------------------------------------------------------------------------------
    // Manager-driven flow (flag on). Uppy is entirely bypassed: files are tracked in `this.files`
    // (a small array of {id, name, type, data, meta} wrappers, mirroring the subset of Uppy's
    // shape the shared preview/progress helpers above rely on) and the actual transfer is handed
    // off to SupercastUploadManager, which owns the Worker + all Rails HTTP calls and survives
    // this controller disconnecting.
    // ---------------------------------------------------------------------------------------

    addFileManaged(file) {
        if (!this.allowedFileTypes.includes(file.type)) {
            if (this.hasErrorTarget) {
                this.errorTarget.innerText = this.fileTypeError(file);
            }
            return;
        }

        // No `id` field here (unlike Uppy's file objects, which use it for
        // removeFile/retryUpload) - the managed path identifies files by `meta.localId` instead,
        // assigned below once SupercastUploadManager.start() returns it.
        const fileWrapper = { name: file.name, type: file.type, data: file, meta: {} };

        // Pull media duration for metadata, same as onBeforeFileAdded does for the legacy path.
        // Parsing is normally near-instant, but isn't guaranteed to resolve before
        // SupercastUploadManager.start() reads `fileWrapper.meta.duration` below, so we also patch
        // it in after the fact via updateUpload() once/if it resolves later.
        extractDuration(file, (duration) => {
            fileWrapper.meta.duration = duration;
            if (fileWrapper.meta.localId) {
                SupercastUploadManager.updateUpload(fileWrapper.meta.localId, { duration });
            }
        });

        // Look up (and purge) any existing file of the same media type *before* registering the
        // new one - registerFileWrapper is also used by reattachUpload to idempotently re-render
        // an already-tracked upload after a Turbo navigation, so the purge can't live there
        // without risking a false "replace" the first time an in-flight upload is reattached.
        const replace = VIDEO_TYPES.includes(fileWrapper.type)
            ? this.getFileByType(VIDEO_TYPES)
            : this.getFileByType(AUDIO_TYPES);
        if (replace) this.purgeReplacedMedia(replace);

        this.registerFileWrapper(fileWrapper);

        this.fileAddedManaged(fileWrapper);
    }

    // Splices out any existing entry of the same media type, tracks the new one, and refreshes
    // its preview - shared by addFileManaged (brand-new file) and reattachUpload (rehydrating a
    // file the manager already knows about after a Turbo navigation).
    registerFileWrapper(fileWrapper) {
        const replace = VIDEO_TYPES.includes(fileWrapper.type)
            ? this.getFileByType(VIDEO_TYPES)
            : this.getFileByType(AUDIO_TYPES);
        if (replace) this.files.splice(this.files.indexOf(replace), 1);
        this.files.push(fileWrapper);

        if (VIDEO_TYPES.includes(fileWrapper.type)) {
            this.updateVideoPreview(fileWrapper);
        } else {
            this.updateAudioPreview(fileWrapper);
        }
    }

    fileAddedManaged(fileWrapper) {
        if (this.hasWorkingTarget) {
            this.workingTarget.classList.remove("hidden");
        }

        const mediaType = fileWrapper.type.split("/")[0];

        const localId = SupercastUploadManager.start({
            file: fileWrapper.data,
            episodeId: this.hasEpisodeIdValue ? this.episodeIdValue : null,
            mediaType,
            dynamic: this.hasDynamicValue ? this.dynamicValue : undefined,
            triggerEvent: this.hasTriggerEventValue ? this.triggerEventValue : undefined,
            duration: fileWrapper.meta.duration,
        });
        fileWrapper.meta.localId = localId;
        this._managedLocalIds[mediaType] = localId;

        this.uploading.push(localId);

        // Core UX change: the Publish button is no longer blocked on the raw upload finishing -
        // the episode can be saved/published while it's still transferring in the background
        // (backend guards this via Episode#upload_in_progress?/#publish_allowed?). Un-hide it the
        // moment the upload *starts*, not only once it completes.
        this.showUploadedTargets.forEach(t => t.hidden = false);
    }

    // Re-renders any upload already tracked by the manager for this episode - what makes
    // disconnect/reconnect across a Turbo navigation safe, since upload state lives in the
    // manager singleton, not this controller instance.
    reattachInFlightUploads() {
        if (!this.hasEpisodeIdValue || !this.episodeIdValue) return;

        SupercastUploadManager.getUploadsForEpisode(this.episodeIdValue).forEach((upload) => this.reattachUpload(upload));
    }

    reattachUpload(upload) {
        if (!upload.file) return; // Cached File didn't survive (e.g. a hard reload) - nothing to preview.

        const fileWrapper = {
            name: upload.file.name,
            type: upload.file.type,
            data: upload.file,
            meta: { localId: upload.localId, upload_log_id: upload.uploadLogId, duration: upload.duration },
        };

        this.registerFileWrapper(fileWrapper);

        const kind = upload.mediaType === "video" ? "video" : "audio";
        this._managedLocalIds[kind] = upload.localId;

        if (upload.state === "uploading" || upload.state === "processing") {
            this.uploading.push(upload.localId);
            this.showUploadedTargets.forEach(t => t.hidden = false);
            this.individualProgressManaged(upload);
            if (upload.state === "uploading") this._showPauseButton(kind);
        } else if (upload.state === "paused") {
            this.uploading.push(upload.localId);
            this.showUploadedTargets.forEach(t => t.hidden = false);
            this._renderPausedProgress(kind, upload);
        } else if (upload.state === "completed") {
            this.onManagedUploadCommitted(upload);
        } else if (upload.state === "error") {
            this.onManagedUploadFailed(upload);
        }
    }

    handleManagerEvent(event) {
        const { type, upload } = event;
        if (!upload) return;

        const belongsToThisEpisode =
            (this.hasEpisodeIdValue && this.episodeIdValue && String(upload.episodeId) === String(this.episodeIdValue)) ||
            this.uploading.includes(upload.localId);
        if (!belongsToThisEpisode) return;

        switch (type) {
            case "episode-resolved": {
                // `hasEpisodeIdValue` is false for a brand-new episode (no
                // data-...-episode-id-value attribute yet), which is exactly the case this event
                // exists to resolve - do NOT gate on it, or the very first upload for a new
                // episode never navigates to the next step. Matches the legacy Uppy path's
                // fileAdded(), which always assigns + dispatches unconditionally.
                const changed = this.episodeIdValue !== upload.episodeId;
                this.episodeIdValue = upload.episodeId;

                if (changed && this.hasEventTarget) {
                    const navEvent = new CustomEvent("upload-started", { detail: { episodeId: upload.episodeId } });
                    this.eventTarget.dispatchEvent(navEvent);
                }

                // transportCredentials are now available - show Pause for multipart video uploads.
                const resolvedKind = upload.mediaType === "video" ? "video" : "audio";
                this._showPauseButton(resolvedKind);
                break;
            }
            case "progress":
                this.individualProgressManaged(upload);
                break;
            case "paused": {
                const pausedKind = upload.mediaType === "video" ? "video" : "audio";
                this._renderPausedProgress(pausedKind, upload);
                break;
            }
            case "resumed": {
                const resumedKind = upload.mediaType === "video" ? "video" : "audio";
                this._showPauseButton(resumedKind);
                break;
            }
            case "commit-success":
                this.onManagedUploadCommitted(upload);
                break;
            case "commit-error":
            case "upload-error":
                this.onManagedUploadFailed(upload);
                break;
            default:
                break;
        }
    }

    individualProgressManaged(upload) {
        // Ignore straggler progress ticks that arrive after a pause was requested - they would
        // overwrite the "Paused · X%" label and reset _mediaStatus to "uploading".
        if (upload.state !== "uploading") return;

        const fileLike = {
            id: upload.localId,
            type: upload.file.type,
            meta: { upload_log_id: upload.uploadLogId },
        };
        this.individualProgress(fileLike, { bytesUploaded: upload.bytesUploaded, bytesTotal: upload.bytesTotal });
    }

    onManagedUploadCommitted(upload) {
        const isVideo = upload.mediaType === "video";
        const kind = isVideo ? "video" : "audio";

        // Scoped to the media type that actually finished
        this._settleMedia(kind, "uploaded");
        this[`${kind}PauseResumeTargets`].forEach(t => t.hidden = true);
        this[`${kind}RetryTargets`].forEach(t => t.hidden = true);

        this.uploading = this.uploading.filter(id => id !== upload.localId);
        if (this.uploading.length == 0) {
            this.showUploadedTargets.forEach(t => t.hidden = false);
        }

        // If a video was uploaded and audio extraction feature is enabled, start polling status
        if (isVideo) {
            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        }
    }

    onManagedUploadFailed(upload) {
        const kind = upload.mediaType === "video" ? "video" : "audio";
        this._settleMedia(kind, "error");
        this[`${kind}PauseResumeTargets`].forEach(t => t.hidden = true);
        this[`${kind}RetryTargets`].forEach(t => { t.hidden = false; t.dataset.kind = kind; });
    }

    retryManagedUpload(e) {
        const kind = e.currentTarget.dataset.kind;
        const localId = this._managedLocalIds[kind];
        if (!localId) return;

        SupercastUploadManager.retry(localId);
    }

    togglePauseManaged(e) {
        const kind = e.currentTarget.dataset.kind;
        const localId = this._managedLocalIds[kind];
        if (!localId) return;

        const upload = SupercastUploadManager.getUpload(localId);
        if (!upload) return;

        if (upload.state === "uploading") {
            SupercastUploadManager.pause(localId);
        } else if (upload.state === "paused") {
            SupercastUploadManager.resume(localId);
        }
    }

    // Shows the Pause button and hides Retry for a given kind (called on upload start / resume).
    _showPauseButton(kind) {
        const upload = SupercastUploadManager.getUpload(this._managedLocalIds[kind]);
        const isMultipart = upload && upload.provider === "aws" && upload.transportCredentials?.upload_mode === "multipart";
        this[`${kind}PauseResumeTargets`].forEach(t => {
            t.hidden = !isMultipart;
            t.textContent = "Pause";
        });
        this[`${kind}RetryTargets`].forEach(t => t.hidden = true);
    }

    // Renders "Paused · X%" in the progress slot and flips the button to "Resume".
    _renderPausedProgress(kind, upload) {
        const percentage = upload.bytesTotal ? Math.round((upload.bytesUploaded / upload.bytesTotal) * 100) : 0;
        this[`${kind}ProgressTargets`].forEach(t => {
            t.innerText = `Paused · ${percentage}%`;
            t.hidden = false;
        });
        this[`${kind}MetaTargets`].forEach(t => t.hidden = true);
        this[`${kind}PauseResumeTargets`].forEach(t => {
            t.hidden = false;
            t.textContent = "Resume";
        });
    }

    // The one progress entry point, reached by both upload paths - the legacy Uppy flow binds it
    // to `upload-progress`, the manager flow routes its worker events through
    // individualProgressManaged. There is deliberately no second, whole-episode progress handler:
    // Uppy's own `progress` event fires only on the legacy path, so anything wired to it was
    // silently dead for every channel on background uploads.
    individualProgress(file, progress) {
        const kind = VIDEO_TYPES.includes(file.type) ? "video" : "audio";
        const percentage = Math.round(progress.bytesUploaded / progress.bytesTotal * 100);

        this._mediaStatus[kind] = {
            state: "uploading",
            bytesUploaded: progress.bytesUploaded,
            bytesTotal: progress.bytesTotal,
        };
        this._renderMediaStatus();
        this._renderMediaProgress(kind, percentage, this._statsText(file, progress));

        if (percentage == 100) {
            // Under the manager-driven flow, log_upload("processing_started") is already fired by
            // SupercastUploadManager itself as soon as the worker reports upload-complete.
            if (!this.useManager) {
                this.logUploadState(file.meta.upload_log_id, "processing_started");
            }
            delete this._uploadStats[file.id];
        }
    }

    // The header pill speaks for the episode, not for one file, so precedence lives here and
    // nowhere else: uploading beats error beats uploaded. Toggled independently per event, a
    // finished video announced "Uploaded" while the audio next to it was still going.
    _renderMediaStatus() {
        const known = Object.values(this._mediaStatus).filter(Boolean);
        if (known.length === 0) return; // Nothing JS has an opinion on - the server render stands.

        const live = known.filter(s => s.state !== "absent");
        const uploading = live.some(s => s.state === "uploading");
        const errored = !uploading && live.some(s => s.state === "error");
        const uploaded = !uploading && !errored && live.some(s => s.state === "uploaded");

        this.mediaUploadingTargets.forEach(t => t.hidden = !uploading);
        this.mediaErrorTargets.forEach(t => t.hidden = !errored);
        this.mediaUploadedTargets.forEach(t => t.hidden = !uploaded);

        if (uploading) {
            // Only what's still transferring. A finished upload keeps its byte counts, and
            // counting them would put a 1GB committed video behind a 40MB audio that's just
            // started - "Uploading... 96%" crawling to 100% for the whole audio transfer, on the
            // add-video-then-audio order the upload step actually recommends.
            const label = `Uploading... ${this._overallPercentage(live.filter(s => s.state === "uploading"))}%`;
            this.mediaUploadPercentageTargets.forEach(t => t.innerText = label);
            // Legacy path only. Under the manager flow the transfer outlives the modal, so Publish
            // is deliberately un-hidden the moment an upload starts (see addFileManaged) and must
            // not be taken away again here.
            if (!this.useManager) this.showUploadedTargets.forEach(t => t.hidden = true);
        }
    }

    // Bytes rather than an average of percentages: a 2GB video next to a 20MB audio shouldn't
    // read 50% when the video has barely started.
    _overallPercentage(states) {
        const total = states.reduce((sum, s) => sum + (s.bytesTotal || 0), 0);
        if (!total) return 0;

        const done = states.reduce((sum, s) => sum + (s.bytesUploaded || 0), 0);
        return Math.round(done / total * 100);
    }

    // Terminal state for one media type - its progress row has nothing left to say, and the
    // header pill re-derives across every type at once.
    _settleMedia(kind, state) {
        this._mediaStatus[kind] = { ...(this._mediaStatus[kind] || {}), state };
        this[`${kind}ProgressTargets`].forEach(t => t.hidden = true);
        // Only "error" leaves the episode holding exactly what the heading already describes, so
        // only "error" may bring that heading back. "uploaded" would resurrect the replaced file's
        // size and duration, and "absent" would advertise media that was just deleted. Both wait
        // for the next server render, which is the only thing that knows the new numbers.
        this[`${kind}MetaTargets`].forEach(t => t.hidden = state !== "error");
        this._renderMediaStatus();
    }

    // One line, in the slot the heading's size/duration already occupies - so an upload starting
    // costs no vertical space, it just changes what the right-hand corner is saying. The stats
    // half stays empty until the sample warms up, hence the two shapes.
    _renderMediaProgress(kind, percentage, statsText) {
        const text = statsText ? `${percentage}% · ${statsText}` : `${percentage}%`;

        this[`${kind}ProgressTargets`].forEach(t => { t.innerText = text; t.hidden = false; });
        // The heading's size/duration describes the file being replaced, not the incoming one.
        this[`${kind}MetaTargets`].forEach(t => t.hidden = true);
    }

    // Speed + ETA for one in-flight file. Was video-only; the numbers are just as useful against
    // an audio upload, and there was never a reason for the two to read differently.
    _statsText(file, progress) {
        if (progress.bytesUploaded >= progress.bytesTotal) return "";

        const now = Date.now();
        const stats = this._getOrInitStats(file.id, now);
        const dt = (now - stats.lastTime) / 1000;
        const minSampleS = 0.5;
        if (dt >= minSampleS) {
            const inst = (progress.bytesUploaded - stats.lastBytes) / dt;
            stats.speedBps = this._emaBps(stats.speedBps, inst); // moving average
            stats.lastTime = now;
            stats.lastBytes = progress.bytesUploaded;
        }

        const elapsedS = (now - stats.startedAt) / 1000;
        const hybridBps = this._hybridBps(stats.speedBps, this._avgBps(progress.bytesUploaded, elapsedS));
        // Stay quiet until there's enough of a sample to be worth trusting - 3 seconds and 5MB.
        if (!this._shouldShowStats(elapsedS, progress.bytesUploaded) || hybridBps <= 0) return "";

        const secondsRemaining = (progress.bytesTotal - progress.bytesUploaded) / hybridBps;
        return `${this._formatBytes(hybridBps)}/s · about ${this._formatDuration(secondsRemaining)} left`;
    }

    async commitUpload(file, _) {
        let csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);

        const metadata = {
            size: file.size,
            filename: file.name,
            mime_type: file.type,
            duration: file.meta.duration,
        };
        if (VIDEO_TYPES.includes(file.type)) {
            formData.append("episode[podcast_video]", file.meta.bunny.video_id);
            formData.append("episode[podcast_video_meta]", JSON.stringify(metadata));
        } else {
            formData.append("episode[podcast_audio]", file.meta.bunny.video_id);
            formData.append("episode[podcast_audio_meta]", JSON.stringify(metadata));
        }
        formData.append("upload_log_id", file.meta.upload_log_id);

        const payload = {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
            },
            body: formData,
        };

        let attempts = 0;
        let response;
        while (attempts < 3) {
            response = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}`, payload);
            if (response.ok) break; // Exit the loop if the request was successful
            attempts++;
        }

        if (!response.ok) {
            this.logUploadState(file.meta.upload_log_id, "error", `Commit failed with status ${response.status}`);
            this.retryUpload(file, null, null);
            return;
        }

        // Only the type that just committed. This used to mark both, so an audio-only commit put
        // a green "Uploaded" against a video the episode didn't have.
        this._settleMedia(VIDEO_TYPES.includes(file.type) ? "video" : "audio", "uploaded");

        this.uploading.splice(this.uploading.indexOf(file.meta.upload_log_id), 1);
        if (this.uploading.length == 0) {
            this.showUploadedTargets.forEach(t => t.hidden = false);
        }

        // If a video was uploaded and audio extraction feature is enabled, start polling status
        if (VIDEO_TYPES.includes(file.type)) {
            // Mark extracting now; controller endpoint will have queued the job on update
            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        }
    }

    retryUpload(file, error, response) {
        this._settleMedia(AUDIO_TYPES.includes(file.type) ? "audio" : "video", "error");

        if (error) {
            this.logUploadState(file.meta.upload_log_id, "error", error.message);
        }

        // Automatically retry uploads on failure. Upload failures are usually intermittent.
        if (this.uppy) this.uppy.retryUpload(file.id);
    }

    attachTus(uppy) {
        uppy.use(window.Uppy.Tus, {
            endpoint: "https://video.bunnycdn.com/tusupload",
            async onBeforeRequest(req, file) {
                var data = file.meta.bunny

                req.setHeader('AuthorizationSignature', data.signature);
                req.setHeader('AuthorizationExpire', data.expire);
                req.setHeader('VideoId', data.video_id);
                req.setHeader('LibraryId', data.library_id);
                // Custom correlation headers for provider diagnostics
                try {
                    if (file?.meta?.upload_log_id) req.setHeader('X-Upload-Trace-Id', file.meta.upload_log_id);
                } catch (_) {}
            },
        });

        uppy.on('file-added', this.fileAdded.bind(this))
            .on('upload-progress', this.individualProgress.bind(this))
            .on('upload-success', this.commitUpload.bind(this))
            .on('upload-error', this.retryUpload.bind(this))
    }

    // Extraction helpers

    onExtractionComplete() {
        if (this.hasAddAudioTarget) {
            this.addAudioTarget.classList.add("hidden");
        }
        if (this.hasPreviewAudioTarget) {
            this.previewAudioTarget.parentElement.classList.remove("hidden");
        }
        // Same rule as a commit: whatever the heading is showing describes the audio extraction
        // just replaced. Usually a no-op, since extraction runs on episodes without audio and the
        // heading is only rendered when there is some - kept for the case where there was.
        this.audioMetaTargets.forEach(t => t.hidden = true);
    }

    async checkExtractionStatusAndMaybePoll() {

        try {
            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
            if (!res.ok) return;
            const data = await res.json();
            const status = data.status;
            if (["queued", "processing"].includes(status)) {
                this.extracting = true;
                this.toggleExtractionNotice();
                this.startExtractionPolling();
            } else {
                this.extracting = false;
                this.toggleExtractionNotice();
                if (status === "done") {
                    this.onExtractionComplete();
                }
            }
        } catch (e) { /* noop */ }
    }

    startExtractionPolling() {
        if (this._extractionPollTimer) return; // already polling
        const poll = async () => {
            try {
                const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
                if (!res.ok) return;
                const { status } = await res.json();
                if (["queued", "processing"].includes(status)) {
                    this.extracting = true;
                    this.toggleExtractionNotice();
                } else {
                    this.extracting = false;
                    this.toggleExtractionNotice();
                    if (this.hasAudioExtractionHintTarget) {
                        this.audioExtractionHintTarget.classList.add("hidden");
                    }
                    clearInterval(this._extractionPollTimer);
                    this._extractionPollTimer = null;
                    if (status === "done") {
                        this.onExtractionComplete();
                    }
                    try {
                        if (window.Turbo && this.hasEventTarget) {
                            window.Turbo.visit(this._detailPath(this.episodeIdValue), { frame: "step" });
                        }
                    } catch (_) {}
                }
            } catch (e) { /* noop */ }
        };
        poll();
        this._extractionPollTimer = setInterval(poll, 4000);
    }

    async retryExtraction() {
        const hadRetryTarget = this.hasRetryControlTarget;
        if (hadRetryTarget) {
            this.retryControlTarget.classList.add("hidden");
        }

        try {
            const csrfToken = document.getElementsByName("csrf-token")[0].content;
            let formData = new FormData();
            formData.append("authenticity_token", csrfToken);

            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/retry_audio_extraction`, {
                method: "POST",
                headers: { "Accept": "application/json" },
                body: formData
            });
            if (!res.ok) {
                if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
                return;
            }

            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        } catch (_) {
            if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
        }
    }

    async cancelExtractionOrRemoveAudio() {
        // If currently extracting, cancel; otherwise, remove audio if present
        if (this.extracting) {
            try {
                const csrfToken = document.getElementsByName("csrf-token")[0].content;
                let formData = new FormData();
                formData.append("authenticity_token", csrfToken);
                await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/cancel_audio_extraction`, { method: "POST", body: formData, headers: { "Accept": "application/json" } });
            } finally {
                this.extracting = false;
                this.toggleExtractionNotice();
                // Immediately hide the extraction hint on cancel click
                if (this.hasAudioExtractionHintTarget) {
                    this.audioExtractionHintTarget.classList.add("hidden");
                }
                if (this._extractionPollTimer) { clearInterval(this._extractionPollTimer); this._extractionPollTimer = null; }
            }
        } else {
            // Fall back to removing audio via existing action
            await this.removeAudio();
        }
    }

    _getOrInitStats(fileId, now) {
        if (!this._uploadStats[fileId]) {
            this._uploadStats[fileId] = { startedAt: now, lastTime: now, lastBytes: 0, speedBps: 0 };
        }
        return this._uploadStats[fileId];
    }

    _emaBps(prev, inst) {
        return prev ? (prev * 0.7 + inst * 0.3) : inst;
    }

    _avgBps(bytesUploaded, elapsedS) {
        return elapsedS > 0 ? (bytesUploaded / elapsedS) : 0;
    }

    _hybridBps(speedBps, avgBps) {
        return (speedBps && avgBps) ? (0.5 * speedBps + 0.5 * avgBps) : (speedBps || avgBps);
    }

    _shouldShowStats(elapsedS, bytesUploaded) {
        return elapsedS >= 3 && bytesUploaded >= 5 * 1024 * 1024;
    }

    _formatBytes(bytes) {
        if (!isFinite(bytes) || bytes <= 0) return "0 B";
        const units = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const value = bytes / Math.pow(1024, i);
        return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[i]}`;
    }

    _formatDuration(totalSeconds) {
        const s = Math.max(0, Math.round(totalSeconds));
        const hours = Math.floor(s / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const seconds = s % 60;
        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        }
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    }
}

// Legacy `Controller.targets = [...]` rather than `static targets` - a name missing from this list
// gives `hasFooTarget === undefined` and `fooTargets === undefined`, with no error to point at it,
// and yarn lint doesn't reach this file (it only globs app/frontend/**/*.js). Keep it in step with
// the markup in _status/_media/visibility.
UploadController.targets = [
    "event", "dropZone", "working", "error", "previewVideo", "previewAudio",
    "videoImageInput", "audioImageInput",
    "showUploaded",
    "mediaStatus", "mediaUploading", "mediaUploadPercentage", "mediaError", "mediaUploaded",
    "videoProgress", "videoMeta", "audioProgress", "audioMeta",
    "videoPauseResume", "audioPauseResume", "videoRetry", "audioRetry",
    "extractionNotice", "videoOnly", "audioOnly", "retryControl", "addVideo", "addAudio",
    "audioExtractionHint"
];
UploadController.values = { episodeId: Number, dynamic: Boolean, triggerEvent: String, releasePlan: String, videoRequired: Boolean };
